import React, {useState} from 'react';
import {useNavigate, useParams} from "react-router-dom";
import {useQuery} from "react-query";
import ngage from "services/ngage";
import {Event} from "types/Event";
import {useNotifications} from "context/NotificationContext";
import Button from "components/Button";
import {PencilIcon, StarIcon, TrashIcon} from "@heroicons/react/solid";
import Page from "components/Page";
import Card from "components/Card";
import DescriptionList from "components/DescriptionList";
import DescriptionListItem from "components/DescriptionListItem";
import DescriptionTerm from "components/DescriptionTerm";
import DescriptionDetails from "components/DescriptionDetails";
import DeleteEvent from "pages/Event/Delete";
import {formattedDate, formattedDateTime, isSameTimezone} from 'services/date';
import {toTitleCase} from "services/string";
import {Frequency as RRuleFrequency} from "rrule";
import Chip from "components/Chip";
import {useOperator} from "context/OperatorContext";
import BooleanIndicator from "../../../components/BooleanIndicator";
import {
  ChatAltIcon,
  ExternalLinkIcon,
  InboxIcon,
  PresentationChartLineIcon,
  ThumbDownIcon,
  ThumbUpIcon
} from "@heroicons/react/outline";
import {FeedbackSubjectType} from "../../../types/FeedbackSubjectType";
import CommentList from "../../../components/CommentList";
import Paginator from "../../../components/Paginator";
import StatList from "../../../components/StatList";
import StatBox from "../../../components/StatBox";
import {useAuth} from "../../../context/AuthContext";
import NotAuthorised from "../../NotAuthorised";

// Helpers ----

const frequencyNames: Record<RRuleFrequency, string> = {
  [RRuleFrequency.YEARLY]: 'Yearly',
  [RRuleFrequency.MONTHLY]: 'Monthly',
  [RRuleFrequency.WEEKLY]: 'Weekly',
  [RRuleFrequency.DAILY]: 'Daily',
  [RRuleFrequency.HOURLY]: 'Hourly',
  [RRuleFrequency.MINUTELY]: 'Minutely',
  [RRuleFrequency.SECONDLY]: 'Secondly',
}

function toPercentage(rate?: number): string {
  return (100 * Number(rate)).toFixed(1) + '%';
}

// Component ----

function ViewEvent() {
  const {termFor, settings} = useOperator();
  const auth = useAuth();
  const navigate = useNavigate();
  const {notify} = useNotifications();

  const {id} = useParams();
  const {data: event, isLoading} = useQuery(
    ['event', {id}],
    () => ngage.events.get(parseInt(id as string)),
    {enabled: auth.can('events:read')}
  );

  const [feedbackPage, setFeedbackPage] = useState<number>(1);
  const {data: feedback, isLoading: isLoadingFeedback} = useQuery(
    ['feedback', {subjectType: 'event', subjectId: id, page: feedbackPage}],
    () => ngage.feedback.matching({
      subject_type: FeedbackSubjectType.EVENT,
      subject_id: Number(id),
      has_comments: true,
      sort: '-created_at',
      page: feedbackPage,
    }),
    {enabled: !!event, keepPreviousData: true}
  );

  const [deletingEvent, setDeletingEvent] = useState<Event | null>(null);

  // Prepare action handlers
  function handleDeleteEvent() {
    if (event) setDeletingEvent(event)
  }

  function handleEditEvent() {
    if (id) navigate(`/events/${id}/edit`)
  }

  // Prevent unauthorised viewing
  if (auth.cannot('events:read')) {
    return <NotAuthorised/>
  }

  // Prepare page actions
  const actions = (
    <>
      {auth.can('events:write') &&
        <Button color={Button.colors.WHITE} icon={TrashIcon} onClick={handleDeleteEvent}>Delete</Button>
      }

      {auth.can('events:write') &&
        <Button color={Button.colors.PRIMARY} icon={PencilIcon} onClick={handleEditEvent}>Edit</Button>
      }
    </>
  )

  const sameTimezone = isSameTimezone(event?.timezone);

  return (
    <Page
      title={event?.name || `View ${termFor('Event')}`}
      breadcrumbs={{
        [termFor('Events')]: "/",
        "Schedule": "/events",
        [event?.name || `View ${termFor('Event')}`]: `/events/${id}`
      }}
      actions={actions}
      isLoading={isLoading}
    >
      {event ?
        <>
          {/* Feedback Stats */}
          <section>
            <StatList>
              <StatBox
                name={'Avg. Feedback Score'}
                icon={StarIcon}
                stat={event.aggregated_feedback?.count ? event.aggregated_feedback.mean_score.toFixed(1) : 'N/A'}
              />

              <StatBox
                name={'Positive Feedback'}
                icon={ThumbUpIcon}
                stat={event.aggregated_feedback?.count ? toPercentage(event.aggregated_feedback.positive_rate) : 'N/A'}
              />

              <StatBox
                name={'Negative Feedback'}
                icon={ThumbDownIcon}
                stat={event.aggregated_feedback?.count ? toPercentage(event.aggregated_feedback.negative_rate) : 'N/A'}
              />

              <StatBox
                name={'Feedback'}
                icon={InboxIcon}
                stat={event.aggregated_feedback?.count.toLocaleString() ?? 0}
              />

              <StatBox
                name={'Comments'}
                icon={ChatAltIcon}
                stat={event.aggregated_feedback?.comment_count.toLocaleString() ?? 0}
                url={'#feedback-comments'}
              />

              <StatBox
                name={'Feedback Trend'}
                icon={PresentationChartLineIcon}
                stat={toTitleCase(event.aggregated_feedback?.category_trend ?? 'neutral')}
              />
            </StatList>
          </section>

          {/* Event Information */}
          <section className="mt-4 flex flex-col lg:flex-row-reverse lg:justify-end">
            {event.image ?
              <div
                className="flex justify-center items-center max-h-36 lg:max-h-full shadow overflow-hidden sm:rounded-md sm:mb-2 lg:mb-0 lg:ml-4">
                <img
                  src={event.image?.preview_url}
                  alt={event.name}
                  className="object-cover w-full lg:h-full lg:w-auto"
                />
              </div>
              : null}

            <div className="lg:basis-2/3 lg:flex-shrink-0 lg:flex-grow">
              <Card
                heading={`${termFor('Event')} Information`}
                subheading={`${event.type.name} ${termFor('event')}`}
              >
                <DescriptionList>
                  <DescriptionListItem span={2}>
                    <DescriptionTerm>Description</DescriptionTerm>
                    <DescriptionDetails>
                      <p
                        className="whitespace-pre-line">{event.description ?? `${termFor('Event')} lasting ${event.duration.formatted}, on ${formattedDate(event.starts_at)}`}</p>
                    </DescriptionDetails>
                  </DescriptionListItem>

                  <DescriptionListItem span={2}>
                    <DescriptionTerm>Event timezone</DescriptionTerm>
                    <DescriptionDetails>
                      {`${event.starts_at.setZone(event.timezone).offsetNameLong} (${event.starts_at.setZone(event.timezone).offsetNameShort})`}
                    </DescriptionDetails>
                  </DescriptionListItem>

                  <DescriptionListItem span={1}>
                    <DescriptionTerm>Starts At</DescriptionTerm>
                    {sameTimezone ?
                      <DescriptionDetails>
                        {formattedDateTime(event.starts_at)}
                      </DescriptionDetails> :
                      <>
                        <DescriptionDetails>
                          {formattedDateTime(event.starts_at, true, event.timezone, settings?.localisation.locale)}
                        </DescriptionDetails>

                        <DescriptionDetails>
                          {formattedDateTime(event.starts_at, true, undefined, settings?.localisation.locale)}
                        </DescriptionDetails>
                      </>
                    }
                  </DescriptionListItem>

                  <DescriptionListItem span={1}>
                    <DescriptionTerm>Ends At</DescriptionTerm>
                    <DescriptionDetails>
                      {sameTimezone ?
                        <DescriptionDetails>
                          {formattedDateTime(event.ends_at)}
                        </DescriptionDetails> :
                        <>
                          <DescriptionDetails>
                            {formattedDateTime(event.ends_at, true, event.timezone, settings?.localisation.locale)}
                          </DescriptionDetails>

                          <DescriptionDetails>
                            {formattedDateTime(event.ends_at, true, undefined, settings?.localisation.locale)}
                          </DescriptionDetails>
                        </>
                      }
                    </DescriptionDetails>
                  </DescriptionListItem>

                  <DescriptionListItem span={1}>
                    <DescriptionTerm>Duration</DescriptionTerm>
                    <DescriptionDetails>
                      {event.duration.formatted}
                    </DescriptionDetails>
                  </DescriptionListItem>

                  <DescriptionListItem span={1}>
                    <DescriptionTerm>Repeats</DescriptionTerm>
                    <DescriptionDetails>
                      {event.recurrence ? frequencyNames[event.recurrence.options.freq as RRuleFrequency] : 'Never'}
                    </DescriptionDetails>
                  </DescriptionListItem>

                  <DescriptionListItem span={1}>
                    <DescriptionTerm>Sustainable</DescriptionTerm>
                    <DescriptionDetails>
                      <BooleanIndicator value={event.is_sustainable}/>
                    </DescriptionDetails>
                  </DescriptionListItem>

                  <DescriptionListItem span={1}>
                    <DescriptionTerm>Published</DescriptionTerm>
                    <DescriptionDetails>
                      <BooleanIndicator value={event.is_published}/>
                    </DescriptionDetails>
                  </DescriptionListItem>

                  <DescriptionListItem span={1}>
                    <DescriptionTerm>Featured</DescriptionTerm>
                    <DescriptionDetails>
                      <BooleanIndicator value={event.is_featured}/>
                    </DescriptionDetails>
                  </DescriptionListItem>

                  <DescriptionListItem span={1}>
                    <DescriptionTerm>Priority</DescriptionTerm>
                    <DescriptionDetails>
                      {event.priority ?? 'Normal'}
                    </DescriptionDetails>
                  </DescriptionListItem>

                  <DescriptionListItem span={1}>
                    <DescriptionTerm>Location</DescriptionTerm>
                    <DescriptionDetails>
                      <Chip
                        name={event.location.name}
                        description={toTitleCase(event.location.type)}
                        backgroundSrc={event.location.image?.preview_url}
                        href={`/${event.location.type}s/${event.location.id}`}/>
                    </DescriptionDetails>
                  </DescriptionListItem>

                  {event.links[0] &&
                    <DescriptionListItem span={1}>
                      <DescriptionTerm>Link</DescriptionTerm>
                      <DescriptionDetails>
                        <Chip
                          name={event.links[0].text}
                          href={event.links[0].url}
                          description={event.links[0].url}
                          icon={ExternalLinkIcon}
                        />
                      </DescriptionDetails>
                    </DescriptionListItem>
                  }

                </DescriptionList>
              </Card>
            </div>
          </section>

          {/* Comments */}
          <section id="feedback-comments" className="mt-4">
            <Card heading="Comments" isLoading={isLoadingFeedback}>
              <CommentList feedback={feedback?.data ?? []}/>

              {!!feedback?.data.length &&
                <div className="mt-4">
                  <Paginator data={feedback} setPage={setFeedbackPage} hideBorder/>
                </div>
              }
            </Card>
          </section>
        </>
        : null}

      {/* Delete confirmation modal */}
      <DeleteEvent
        show={event === deletingEvent}
        event={deletingEvent}
        onCancel={() => setDeletingEvent(null)}
        onDelete={event => {
          navigate('/events')
          notify("success", `${termFor('Event')} Deleted`, `${event.name} has been permanently removed`)
        }}
        onDeleteFail={event => {
          setDeletingEvent(null)
          notify("error", `${termFor('Event')} Not Deleted`, `${event.name} could not be removed`)
        }}
      />
    </Page>
  );
}

export default ViewEvent;